import useRedirect, { REDIRECT_QUERY } from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { isGuardExemptPath, resolveProfileStep } from "@/lib/auth/profileGuard";
import { SIGNUP_AFTER_USER_PROFILE, SIGNUP_COMPLETE } from "@/settings/constant";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
/**
 * 전역 프로필 가드.
 * 로그인 유저가 일반 페이지(면제 경로 외)에 진입할 때:
 *  1) 필수 프로필(job/subject/school·grade) 미완료 → 설정 화면으로
 *  2) 프로필 완료 + 가입완료 혜택 미열람(hasSignupBenefitLog === false) → 혜택 페이지로
 * 둘 다 아니면 통과.
 *
 * - 면제 경로(/member 등)·비로그인은 즉시 통과 (무한루프/강제로그인 방지)
 * - 리다이렉트 목적지(profile/complete)는 /member 면제 경로라 가드가 다시 동작하지 않음
 * - hasSignupBenefitLog가 undefined(BE 미연동)면 혜택 게이팅은 비활성 → 안전 기본값
 *
 * @returns blocked - 리다이렉트가 필요해 페이지 콘텐츠를 감춰야 하는지 여부
 */ export const useProfileGuard = ()=>{
    var _user_schoolItems;
    const { user , isLoggedIn  } = useAuthStore();
    const router = useRouter();
    const { redirectPath  } = useRedirect();
    const redirectedTargetRef = useRef(null);
    const exempt = isGuardExemptPath(router.pathname);
    const active = !exempt && isLoggedIn;
    const profileStep = active ? resolveProfileStep(user) : null;
    const needProfile = profileStep !== null;
    const needBenefit = active && !needProfile && (user === null || user === void 0 ? void 0 : user.hasSignupBenefitLog) === false;
    const blocked = needProfile || needBenefit;
    useEffect(()=>{
        if (!active || !router.isReady) {
            redirectedTargetRef.current = null;
            return;
        }
        const target = needProfile ? SIGNUP_AFTER_USER_PROFILE : needBenefit ? SIGNUP_COMPLETE : null;
        if (!target) {
            redirectedTargetRef.current = null;
            return;
        }
        // 동일 목적지 중복 push 방지 (effect 재실행 가드)
        if (router.pathname === target || redirectedTargetRef.current === target) return;
        redirectedTargetRef.current = target;
        router.push({
            pathname: target,
            query: {
                [REDIRECT_QUERY]: redirectPath || router.asPath,
                // 기존 로그인 유저(가입 직후가 아님)임을 표시 → 프로필 완료 후 원래 페이지로 복귀
                ...needProfile ? {
                    alreadySignupUser: true
                } : {}
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        active,
        router.isReady,
        router.pathname,
        needProfile,
        needBenefit,
        user === null || user === void 0 ? void 0 : user.job,
        user === null || user === void 0 ? void 0 : user.mainSubject,
        user === null || user === void 0 ? void 0 : (_user_schoolItems = user.schoolItems) === null || _user_schoolItems === void 0 ? void 0 : _user_schoolItems.length,
        user === null || user === void 0 ? void 0 : user.schoolGrade,
        user === null || user === void 0 ? void 0 : user.hasSignupBenefitLog
    ]);
    return {
        blocked
    };
};
